(() => {
  const editableField = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select';

  // Safety net only: the latch below normally releases when the primary
  // cycles busy or the cart drawer opens. Kept short so a missed signal
  // costs seconds, not the 15s that previously swallowed follow-up taps.
  const FALLBACK_RELEASE_MS = 5000;

  // A touch counts as a tap only if the finger stays within this radius and lifts in time.
  const TAP_SLOP_PX = 10;
  const TAP_MAX_MS = 700;

  const initialize = (root) => {
    if (root.dataset.mobileStickyReady === 'true') return;
    const sticky = root.querySelector('[data-mobile-sticky-cart]');
    if (!sticky) return;
    const primary = root.querySelector(sticky.dataset.primarySelector);
    if (!primary) return;
    root.dataset.mobileStickyReady = 'true';

    // KAN-713 contract: the sticky action submits the same product form as
    // the primary action. No new cart logic — it never forwards to a gift or
    // alternate form, even when such a region is in view. Gift purchases
    // happen only through the gift form's own submit control.
    let pendingClick = false;
    let observedProcessing = false;

    let keyboardViewportSeen = false;
    let keyboardDismissed = false;
    let expandedViewportHeight = window.visualViewport?.height || window.innerHeight;
    const avoidSelector = sticky.dataset.avoidSelector;
    const hideRegionSelector = sticky.dataset.hideWhileVisible;

    const isEditing = () => document.activeElement?.matches(editableField) && !keyboardDismissed;
    const keyboardIsOpen = () => {
      if (!window.visualViewport) return false;
      expandedViewportHeight = Math.max(expandedViewportHeight, window.visualViewport.height);
      return expandedViewportHeight - window.visualViewport.height >= Math.max(120, expandedViewportHeight * 0.18);
    };

    // Dawn's product-form signals processing with the `disabled` attribute
    // and/or a `loading` class on the submitter — not always a `.disabled`
    // class. Watch all three, or the latch below never observes processing.
    const isBusy = () => primary.disabled
      || primary.classList.contains('disabled')
      || primary.classList.contains('loading');

    const releaseLatch = () => {
      if (!pendingClick) return;
      pendingClick = false;
      observedProcessing = false;
    };

    const syncState = () => {
      if (pendingClick && isBusy()) observedProcessing = true;
      if (pendingClick && observedProcessing && !isBusy()) releaseLatch();
      const unavailable = isBusy() || pendingClick;
      sticky.disabled = unavailable;
      sticky.classList.toggle('disabled', unavailable);
      sticky.setAttribute('aria-busy', String(pendingClick));
      sticky.textContent = primary.hasAttribute('disabled')
        ? primary.textContent.trim()
        : sticky.dataset.defaultLabel;
    };

    // Regions the sticky stays out of for as long as ANY part of them is on screen — a
    // separate purchase flow (the At-Home gift section) must never share the screen with
    // this button. Measured against the viewport, not the sticky's own box: the box test
    // below only hides when the button overlaps a control, which left it sitting on the
    // gift tier options. The viewport height takes the largest of the three readings so a
    // collapsing mobile toolbar can only ever widen the hidden range.
    const viewportBottom = () => Math.max(
      window.innerHeight,
      document.documentElement.clientHeight,
      window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : 0,
    );
    const hiddenRegionInView = () => {
      if (!hideRegionSelector) return false;
      const bottom = viewportBottom();
      return Array.from(document.querySelectorAll(hideRegionSelector)).some((element) => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < bottom;
      });
    };

    const syncVisibility = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const passedPrimary = primary.getBoundingClientRect().bottom <= 0;
      const cartOpen = document.querySelector('cart-drawer.is-visible') !== null;
      // The drawer opening means the add landed — never hold the latch past it.
      if (cartOpen) releaseLatch();
      let intersectsControls = false;
      if (mobile && passedPrimary && avoidSelector) {
        const stickyRect = sticky.getBoundingClientRect();
        intersectsControls = Array.from(document.querySelectorAll(avoidSelector)).some((element) => {
          const rect = element.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0
            && rect.left < stickyRect.right && rect.right > stickyRect.left
            && rect.top < stickyRect.bottom && rect.bottom > stickyRect.top;
        });
      }
      const visible = mobile && passedPrimary && !cartOpen && !isEditing()
        && !intersectsControls && !hiddenRegionInView();
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      sticky.tabIndex = visible ? 0 : -1;
      syncState();
    };

    const syncKeyboard = () => {
      if (!window.matchMedia('(max-width: 749px)').matches || !document.activeElement?.matches(editableField)) {
        keyboardViewportSeen = false;
        keyboardDismissed = false;
        expandedViewportHeight = window.visualViewport?.height || window.innerHeight;
      } else if (keyboardIsOpen()) {
        keyboardViewportSeen = true;
        keyboardDismissed = false;
      } else if (keyboardViewportSeen) {
        keyboardDismissed = true;
      }
      syncVisibility();
    };

    const activate = () => {
      if (sticky.disabled || primary.disabled || primary.classList.contains('disabled')) return;
      pendingClick = true;
      observedProcessing = false;
      sticky.disabled = true;
      primary.click();
      requestAnimationFrame(syncState);
      window.setTimeout(() => {
        releaseLatch();
        syncState();
      }, FALLBACK_RELEASE_MS);
    };
    // Mouse, keyboard, and VoiceOver activation all arrive as click.
    sticky.addEventListener('click', activate);

    // iOS WebKit can withhold the click after a clean tap on this button. On-device logging
    // (KAN-713, iPhone 14 Pro, Chrome) recorded touchstart and touchend on the button, 0px of
    // movement, the page at rest for 649ms — and no click, so the first tap did nothing and
    // the second one added to cart. Act on the touch itself, and cancel the synthetic click
    // so a browser that does send one cannot activate twice (the latch would also stop it).
    let touchStart = null;
    sticky.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      touchStart = event.touches.length === 1
        ? { x: touch.clientX, y: touch.clientY, at: event.timeStamp }
        : null;
    }, { passive: true });
    sticky.addEventListener('touchmove', (event) => {
      if (!touchStart) return;
      const touch = event.touches[0];
      if (Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y) > TAP_SLOP_PX) {
        touchStart = null;
      }
    }, { passive: true });
    sticky.addEventListener('touchcancel', () => { touchStart = null; }, { passive: true });
    sticky.addEventListener('touchend', (event) => {
      const start = touchStart;
      touchStart = null;
      if (!start || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const travelled = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
      if (travelled > TAP_SLOP_PX || event.timeStamp - start.at > TAP_MAX_MS) return;
      if (!sticky.classList.contains('is-visible')) return;
      event.preventDefault();
      activate();
    });
    new MutationObserver(syncState).observe(primary, {
      attributes: true,
      attributeFilter: ['class', 'disabled'],
      childList: true,
      characterData: true,
      subtree: true,
    });
    window.addEventListener('scroll', syncVisibility, { passive: true });
    window.addEventListener('resize', syncKeyboard, { passive: true });
    window.visualViewport?.addEventListener('resize', syncKeyboard, { passive: true });
    document.addEventListener('focusin', syncVisibility);
    document.addEventListener('focusout', () => requestAnimationFrame(syncKeyboard));
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      new MutationObserver(syncVisibility).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    syncState();
    syncVisibility();
  };

  const initializeAll = () => document.querySelectorAll('[data-mobile-sticky-root]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll);
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
